/**
 * sl-uq2j.3.1: Session History with Search, Rename, Pinned group.
 *
 * Changes from previous version:
 * - Search input at top (client-side filter on session.title)
 * - Pinned sessions as own group above time groups
 * - Inline rename: double-click title → edit mode → Enter saves
 * - Doc badge removed (sessions are workspace-scoped, no docId)
 */
import type { CopilotChatHistoryFragment } from '@affine/graphql';
import { I18n } from '@affine/i18n';
import { WithDisposable } from '@blocksuite/affine/global/lit';
import { scrollbarStyle } from '@blocksuite/affine/shared/styles';
import { unsafeCSSVarV2 } from '@blocksuite/affine/shared/theme';
import { ShadowlessElement } from '@blocksuite/affine/std';
import { DeleteIcon, EditIcon } from '@blocksuite/icons/lit';
import { css, html, nothing, type PropertyValues } from 'lit';
import { property, query, state } from 'lit/decorators.js';

import { AIProvider } from '../../provider';

interface GroupedSessions {
  pinned: BlockSuitePresets.AIRecentSession[];
  today: BlockSuitePresets.AIRecentSession[];
  last7Days: BlockSuitePresets.AIRecentSession[];
  last30Days: BlockSuitePresets.AIRecentSession[];
  older: BlockSuitePresets.AIRecentSession[];
}

export class AISessionHistory extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    .ai-session-history {
      width: 100%;
      max-height: min(
        520px,
        var(
          --streamlain-disclosure-popover-scroll-max-height,
          calc(100vh - 96px)
        )
      );
      padding: 14px 16px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      color: ${unsafeCSSVarV2('text/primary')};

      .search-container {
        flex: 0 0 auto;
        margin-bottom: 12px;
      }

      .search-input {
        width: 100%;
        box-sizing: border-box;
        height: 40px;
        padding: 0 12px;
        border: 0.5px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
        border-radius: 8px;
        background: ${unsafeCSSVarV2('layer/background/secondary')};
        color: ${unsafeCSSVarV2('text/primary')};
        font-size: 14px;
        line-height: 20px;
        outline: none;
      }

      .search-input::placeholder {
        color: ${unsafeCSSVarV2('text/placeholder')};
      }

      .search-input:focus {
        border-color: ${unsafeCSSVarV2('button/primary')};
      }

      .history-content {
        min-height: 0;
        flex: 1 1 auto;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 0 2px 0 0;
      }

      .loading-container,
      .empty-container {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 180px;
      }

      .loading-title,
      .empty-title {
        font-weight: 600;
        font-size: var(--streamlain-font-sm);
        color: var(--streamlain-text-secondary-color);
      }

      .ai-session-group {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .ai-session-group-title {
        padding: 0 8px;
        font-size: 13px;
        font-weight: 400;
        line-height: 24px;
        color: ${unsafeCSSVarV2('text/secondary')};
      }

      .ai-session-item {
        position: relative;
        display: flex;
        min-height: 32px;
        padding: 0 6px 0 8px;
        gap: 8px;
        justify-content: space-between;
        align-items: center;
        border-radius: 6px;
        cursor: pointer;
      }

      .ai-session-item:hover {
        background: ${unsafeCSSVarV2('layer/background/hoverOverlay')};
      }

      .ai-session-item[aria-selected='true'] .ai-session-title {
        color: ${unsafeCSSVarV2('text/emphasis')};
      }

      .ai-session-title {
        font-size: 14px;
        font-weight: 400;
        line-height: 22px;
        color: ${unsafeCSSVarV2('text/primary')};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        min-width: 0;
      }

      .ai-session-title-edit {
        font-size: 14px;
        font-weight: 400;
        line-height: 22px;
        padding: 2px 6px;
        color: ${unsafeCSSVarV2('text/primary')};
        border: 1px solid ${unsafeCSSVarV2('button/primary')};
        border-radius: 4px;
        outline: none;
        background: ${unsafeCSSVarV2('layer/background/secondary')};
        flex: 1;
        min-width: 0;
      }

      .ai-session-actions {
        display: flex;
        align-items: center;
        gap: 2px;
        flex: 0 0 auto;
        opacity: 0;
        visibility: hidden;
        transition:
          opacity 0.2s ease,
          visibility 0.2s ease;
      }

      .ai-session-item-rename,
      .ai-session-item-delete {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border-radius: 4px;
        cursor: pointer;

        svg {
          width: 16px;
          height: 16px;
          color: ${unsafeCSSVarV2('icon/primary')};
        }
      }

      .ai-session-item-rename:hover,
      .ai-session-item-delete:hover {
        background: ${unsafeCSSVarV2('layer/background/hoverOverlay')};
      }

      .ai-session-item:hover .ai-session-actions,
      .ai-session-item:focus-within .ai-session-actions {
        opacity: 1;
        visibility: visible;
      }
    }

    ${scrollbarStyle('.history-content')}
  `;

  @property({ attribute: false })
  accessor session!: CopilotChatHistoryFragment | null | undefined;

  @property({ attribute: false })
  accessor workspaceId!: string;

  @property({ attribute: false })
  accessor onSessionClick!: (sessionId: string) => void;

  @property({ attribute: false })
  accessor onSessionDelete!: (
    session: BlockSuitePresets.AIRecentSession
  ) => void;

  @property({ attribute: false })
  accessor onSessionRename!:
    | ((options: { sessionId: string; title: string }) => Promise<void>)
    | undefined;

  @query('.ai-session-history')
  accessor scrollContainer!: HTMLElement;

  @state()
  private accessor sessions: BlockSuitePresets.AIRecentSession[] | undefined;

  @state()
  private accessor loadingMore = false;

  @state()
  private accessor hasMore = true;

  @state()
  private accessor searchQuery = '';

  @state()
  private accessor editingSessionId: string | null = null;

  private accessor currentOffset = 0;

  private readonly pageSize = 10;

  private groupSessionsByTime(
    sessions: BlockSuitePresets.AIRecentSession[]
  ): GroupedSessions {
    const now = new Date();
    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const last7DaysStart = new Date(
      todayStart.getTime() - 6 * 24 * 60 * 60 * 1000
    );
    const last30DaysStart = new Date(
      todayStart.getTime() - 29 * 24 * 60 * 60 * 1000
    );

    const grouped: GroupedSessions = {
      pinned: [],
      today: [],
      last7Days: [],
      last30Days: [],
      older: [],
    };

    sessions.forEach(session => {
      // sl-uq2j: Pinned sessions go to their own group
      if (session.pinned) {
        grouped.pinned.push(session);
        return;
      }

      const updatedAt = new Date(session.updatedAt);
      if (updatedAt >= todayStart) {
        grouped.today.push(session);
      } else if (updatedAt >= last7DaysStart) {
        grouped.last7Days.push(session);
      } else if (updatedAt >= last30DaysStart) {
        grouped.last30Days.push(session);
      } else {
        grouped.older.push(session);
      }
    });

    // Sort each group by updatedAt descending
    (Object.keys(grouped) as Array<keyof GroupedSessions>).forEach(key => {
      grouped[key].sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
    });

    return grouped;
  }

  private getFilteredSessions(): BlockSuitePresets.AIRecentSession[] {
    if (!this.sessions) return [];
    if (!this.searchQuery.trim()) return this.sessions;
    const q = this.searchQuery.trim().toLowerCase();
    return this.sessions.filter(s =>
      (s.title || I18n.t('com.streamlain.ai.chat.history.default-title'))
        .toLowerCase()
        .includes(q)
    );
  }

  private async getRecentSessions() {
    this.loadingMore = true;

    const moreSessions =
      (await AIProvider.session?.getRecentSessions(
        this.workspaceId,
        this.pageSize,
        this.currentOffset
      )) || [];
    this.sessions = [...(this.sessions || []), ...moreSessions];

    this.currentOffset += moreSessions.length;
    this.hasMore = moreSessions.length === this.pageSize;
    this.loadingMore = false;
  }

  private readonly onScroll = () => {
    if (!this.hasMore || this.loadingMore) {
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = this.scrollContainer;
    const threshold = 50;
    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      this.getRecentSessions().catch(console.error);
    }
  };

  private readonly onSearchInput = (e: InputEvent) => {
    this.searchQuery = (e.target as HTMLInputElement).value;
  };

  private readonly startRename = (sessionId: string) => {
    this.editingSessionId = sessionId;
  };

  private readonly finishRename = async (
    sessionId: string,
    newTitle: string
  ) => {
    this.editingSessionId = null;
    const trimmed = newTitle.trim();
    if (!trimmed || !this.onSessionRename) return;
    await this.onSessionRename({ sessionId, title: trimmed });
    // Update local state optimistically
    if (this.sessions) {
      this.sessions = this.sessions.map(s =>
        s.sessionId === sessionId ? { ...s, title: trimmed } : s
      );
    }
  };

  override connectedCallback() {
    super.connectedCallback();
    this.getRecentSessions().catch(console.error);
  }

  override firstUpdated(changedProperties: PropertyValues) {
    super.firstUpdated(changedProperties);
    this.disposables.add(() => {
      this.scrollContainer.removeEventListener('scroll', this.onScroll);
    });
    this.scrollContainer.addEventListener('scroll', this.onScroll);
  }

  private renderSessionGroup(
    title: string,
    sessions: BlockSuitePresets.AIRecentSession[]
  ) {
    if (sessions.length === 0) {
      return nothing;
    }
    return html`
      <div class="ai-session-group">
        <div class="ai-session-group-title">${title}</div>
        ${sessions.map(session => {
          const isEditing = this.editingSessionId === session.sessionId;
          const displayTitle =
            session.title ||
            I18n.t('com.streamlain.ai.chat.history.default-title');

          return html`
            <div
              class="ai-session-item"
              @click=${(e: MouseEvent) => {
                e.stopPropagation();
                if (!isEditing) this.onSessionClick(session.sessionId);
              }}
              aria-selected=${this.session?.sessionId === session.sessionId}
              data-session-id=${session.sessionId}
            >
              ${isEditing
                ? html`<input
                    class="ai-session-title-edit"
                    .value=${displayTitle}
                    @keydown=${(e: KeyboardEvent) => {
                      if (e.key === 'Enter') {
                        this.finishRename(
                          session.sessionId,
                          (e.target as HTMLInputElement).value
                        ).catch(console.error);
                      } else if (e.key === 'Escape') {
                        this.editingSessionId = null;
                      }
                    }}
                    @blur=${(e: FocusEvent) => {
                      this.finishRename(
                        session.sessionId,
                        (e.target as HTMLInputElement).value
                      ).catch(console.error);
                    }}
                    @click=${(e: MouseEvent) => e.stopPropagation()}
                  />`
                : html`<div
                    class="ai-session-title"
                    @dblclick=${(e: MouseEvent) => {
                      e.stopPropagation();
                      this.startRename(session.sessionId);
                    }}
                  >
                    ${displayTitle}
                    <affine-tooltip .offsetX=${60}>
                      ${I18n.t('com.streamlain.ai.chat.history.open-rename')}
                    </affine-tooltip>
                  </div>`}
              <div class="ai-session-actions">
                <div
                  class="ai-session-item-rename"
                  @click=${(e: MouseEvent) => {
                    e.stopPropagation();
                    this.startRename(session.sessionId);
                  }}
                >
                  ${EditIcon()}
                  <affine-tooltip>
                    ${I18n.t('com.streamlain.ai.chat.history.rename')}
                  </affine-tooltip>
                </div>
                <div
                  class="ai-session-item-delete"
                  @click=${(e: MouseEvent) => {
                    e.stopPropagation();
                    this.onSessionDelete(session);
                  }}
                >
                  ${DeleteIcon()}
                  <affine-tooltip>
                    ${I18n.t('com.streamlain.ai.chat.history.delete')}
                  </affine-tooltip>
                </div>
              </div>
            </div>
          `;
        })}
      </div>
    `;
  }

  // sl-uq2j: Auto-focus the rename input after render
  override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (changedProperties.has('editingSessionId') && this.editingSessionId) {
      const input = this.renderRoot?.querySelector(
        '.ai-session-title-edit'
      ) as HTMLInputElement | null;
      if (input) {
        input.focus();
        input.select();
      }
    }
  }

  private renderLoading() {
    return html`
      <div class="loading-container">
        <div class="loading-title">
          ${I18n.t('com.streamlain.ai.chat.history.loading')}
        </div>
      </div>
    `;
  }

  private renderEmpty() {
    return html`
      <div class="empty-container">
        <div class="empty-title">
          ${I18n.t('com.streamlain.ai.chat.history.empty')}
        </div>
      </div>
    `;
  }

  private renderHistory() {
    if (!this.sessions) {
      return this.renderLoading();
    }

    const filtered = this.getFilteredSessions();

    if (filtered.length === 0) {
      return this.renderEmpty();
    }

    const groupedSessions = this.groupSessionsByTime(filtered);
    return html`
      ${this.renderSessionGroup(
        I18n.t('com.streamlain.ai.chat.history.pinned'),
        groupedSessions.pinned
      )}
      ${this.renderSessionGroup(
        I18n.t('com.streamlain.ai.chat.history.today'),
        groupedSessions.today
      )}
      ${this.renderSessionGroup(
        I18n.t('com.streamlain.ai.chat.history.last-7-days'),
        groupedSessions.last7Days
      )}
      ${this.renderSessionGroup(
        I18n.t('com.streamlain.ai.chat.history.last-30-days'),
        groupedSessions.last30Days
      )}
      ${this.renderSessionGroup(
        I18n.t('com.streamlain.ai.chat.history.older'),
        groupedSessions.older
      )}
    `;
  }

  override render() {
    return html`
      <div class="ai-session-history">
        <div class="search-container">
          <input
            class="search-input"
            type="text"
            placeholder=${I18n.t(
              'com.streamlain.ai.chat.history.search-placeholder'
            )}
            .value=${this.searchQuery}
            @input=${this.onSearchInput}
          />
        </div>
        <div class="history-content streamlain-disclosure-popover-scroll">
          ${this.renderHistory()}
        </div>
      </div>
    `;
  }
}
